/**
 * Which storage form a library asset row is in, read off the row itself.
 *
 * Two forms coexist in the library for as long as the rollout switch is on
 * (CONTENT_MODEL_SPEC §6.1):
 *
 * - a **folder** asset — `trace-content/assets/{id}/<hash>.gltf`, the
 *   document the server writes in folder mode. Its live address is
 *   `GET /assets/{id}/file`, which 302s to the current document, so a scene
 *   that names it follows the asset through every edit (§3.4).
 * - a **legacy** asset — a raw blob in `trace-cms-assets/<guid>.<ext>`: every
 *   row written before the content model, and every row written while
 *   `ContentOptions:LegacyWrites` is on. Its bytes are addressed by the row's
 *   own url, exactly as they always were, and nothing re-resolves them.
 *
 * The decision has to be per **row**, not per environment. A client that
 * minted `/file` for every row broke against an API without the endpoint
 * (prod = release #885 answers 401), and one that never minted it would
 * freeze every folder asset to one document hash. The one thing the DTO
 * carries that says where the bytes live is the container in `url`, so that
 * is the signal — absolute or blob-relative, the container is a path
 * segment either way.
 *
 * Pure module: no viewer, no I/O.
 */

import { TRACE_CONTENT_CONTAINER } from './scene-folder';

export type AssetStorage = 'folder' | 'legacy';

/** `trace-content/` as a whole path segment, anywhere in the path. */
const FOLDER_CONTAINER_RE = new RegExp(`(^|/)${TRACE_CONTENT_CONTAINER}/`, 'i');

/**
 * The path of `url`, whether it is absolute or blob-relative.
 *
 * Only an http(s) URL is parsed as one: `file:///trace-content/x` and a bare
 * `/trace-content/x` both parse as absolute URIs with an empty host, and the
 * raw string is the honest answer for those (see the `file:` landmine in
 * trace-viewer's `content/assetRef.ts`).
 */
function pathOf(url: string): string {
    if (/^https?:\/\//i.test(url)) {
        try {
            return new URL(url).pathname;
        } catch {
            // Fall through to the raw string.
        }
    }
    return url.split(/[?#]/, 1)[0];
}

/** Whether a row's `url` names a file in a content folder (spec §2.1). */
export function isFolderAssetUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return FOLDER_CONTAINER_RE.test(pathOf(url));
}

/** The storage form of a row with this `url`. A row with no url is legacy: there is no folder to be in. */
export function assetStorageOf(url: string | null | undefined): AssetStorage {
    return isFolderAssetUrl(url) ? 'folder' : 'legacy';
}
